<template>
  <div
    :class="[
      'SparkCard',
      {
        'high-aspect': isHighAspectRatio,
        'is-mobile': isMobile,
        'is-joined': isJoined,
        'is-proposal': isProposal,
        'is-expired': isExpired,
      },
    ]"
  >
    <div class="cover" @click="showLocationActions = !showLocationActions">
      <LocationCover :location="segment.location" :is-colorful="isProposal" class="location-image" />

      <Transition name="location-actions">
        <div v-if="showLocationActions" class="location-actions-overlay" @click.stop="showLocationActions = false">
          <div class="location-action-btn" @click.stop="handleLocationDetailsClick">
            <GamIcon :name="GamIconName.LOCATION" size="30px" is-current />
            <span>{{ localize('card.action.spot') }}</span>
          </div>
          <div class="location-action-btn" @click.stop="handleNavigateClick">
            <GamIcon :name="GamIconName.NAVIGATE" size="30px" is-current />
            <span>{{ localize('card.action.navigate') }}</span>
          </div>
        </div>
      </Transition>

      <div v-if="!isExpired && isPWA && isProposal" class="cover-actions">
        <div class="card-action" @click.stop="shareProposal">
          <GamIcon :name="GamIconName.SHARE" size="17px" is-current />
        </div>
      </div>

      <div class="cover-foot">
        <div class="cover-players" :key="playerSlots.filter((slot) => !!slot.player).length">
          <div
            v-for="(slot, p) in playerSlots"
            class="seat"
            :key="`player-${slot.player?.id || slot.player?.username || p}`"
          >
            <div
              v-if="p === playerSlots.length - 1 && proposal?.id"
              class="seat-ring is-invite"
              @click.stop="handleOpenInviteModal"
            >
              <span v-if="myInviteeCount" class="seat-count">{{ myInviteeCount }}</span>
              <GamIcon v-else :name="GamIconName.PLUS" size="20px" is-current />
            </div>

            <PlayerAvatar
              v-else-if="slot.player"
              :player="slot.player"
              :is-confirmed="slot.isConfirmed"
              :is-invited="slot.isInvited"
              :is-up-for-meeting="slot.isUpForMeeting"
              is-stacked
              hide-rating
              :key="`avatar-${slot.player?.id || slot.player?.username}`"
              @click.stop="handleAvatarClick(slot.player)"
            />

            <div v-else class="seat-ring" />
          </div>
        </div>
      </div>
    </div>

    <div class="chip-band">
      <span v-if="displayTitle" class="band-title">{{ displayTitle }}</span>
      <GamChip v-for="(tag, index) in proposalBandTags" v-bind="tag" :key="`proposal-band-${index}`" />
    </div>

    <div class="meta" :class="{ 'needs-respond': isInvitedToProposal }" @click="dismissLocationActions">
      <div class="meta-head">
        <div class="when">
          <span v-if="sparkDateType === GamDateType.NOW" class="day day-live">NOW</span>
          <span v-else-if="sparkDateType === GamDateType.SOON" class="day day-live">SOON</span>
          <span v-else-if="isDisplayToday" class="day day-live">Today</span>
          <span v-else-if="isDisplayTomorrow" class="day day-live">Tomorrow</span>
          <span v-else class="day">{{ dayLabel }}</span>
          <span class="sep">&middot;</span>
          <span class="time">{{ startLabel }}</span>
        </div>

        <div class="sub">
          <span class="spot" @click.stop="showLocationActions = !showLocationActions">
            {{ segment.location.name }}
          </span>

          <template v-if="proposal?.club">
            <span class="sep">&middot;</span>
            <span class="club-link" @click.stop="handleNavigateToClub">@{{ proposal.club.username }}</span>
          </template>

          <template v-if="statusLabel">
            <span class="sep">&middot;</span>
            <span class="status">
              <span v-if="isNeededStatus" class="dot" />
              {{ statusLabel }}
            </span>
          </template>
        </div>

        <div v-if="conflictingGambit" class="conflict-warning">
          <GamIcon :name="GamIconName.WARNING" size="12px" is-current />
          <span>{{
            localizeWithValues('sparks.conflict.attending', getAttendanceConflictLabel(conflictingGambit))
          }}</span>
        </div>
      </div>

      <div class="controls">
        <div class="tertiary">
          <template v-if="showChessSetAction">
            <div
              v-if="userConfirmation"
              :class="[
                'chess-set-action',
                'has-status',
                { 'is-on': activeChessSetCount > 0, 'has-warning': isShortOfChessSets },
              ]"
              @click.stop="handleOpenChessSetPopover"
            >
              <GamIcon :name="GamIconName.CHESS_SET" size="20px" is-current />
            </div>

            <GamChessSetCounter v-else v-model="chessSetCount" />
          </template>
        </div>

        <div class="primary">
          <div v-if="isExpired" class="expired-notice">Expired</div>

          <div v-else-if="proposal?.isSuccessful" class="skeleton-button" />

          <GamButton
            v-else-if="userConfirmation"
            class="action icon-circle play-cta play-cta-proposal is-inverted"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.LARGE"
            :left-icon="GamIconName.PAUSE"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            is-icon-only
            @click="handleLeaveProposal"
          />

          <GamButton
            v-else-if="isInvitedToProposal"
            class="action play-cta play-cta-proposal"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.SMALL"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            @click="handleAcceptInvitation"
          >
            Accept
          </GamButton>

          <GamButton
            v-else
            class="action icon-circle play-cta play-cta-proposal"
            :variant="GamButtonVariant.SLAB"
            :size="GamButtonSize.LARGE"
            @click="handleCreateOrJoinProposal(segment, startTime, endTime)"
            :left-icon="GamIconName.PLAY_EMPTY"
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting"
            is-icon-only
          />
        </div>

        <div class="secondary">
          <div v-if="showMeetAnyways" :class="['meet-anyways', { 'hint-above': isVerticallyCondensed }]">
            <GamButton
              :class="[
                'action',
                'icon-circle',
                'meet-anyways-btn',
                'play-cta',
                'play-cta-proposal',
                { 'is-inverted': userHasMeetAnyways },
              ]"
              :variant="GamButtonVariant.SLAB"
              :size="GamButtonSize.SMALL"
              @click="handleMeetAnyways"
              :left-icon="GamIconName.FAST"
              :is-disabled="isSubmitting"
              :is-loading="isSubmitting"
              is-icon-only
            />
            <span class="meet-anyways-hint">{{ localize('spark.card.meet_anyways.hint') }}</span>
          </div>
        </div>
      </div>
    </div>

    <GamChessSetPopover
      v-model:open="showChessSetPopover"
      v-model="editChessSetCount"
      :player-chess-sets="playerChessSets"
      :spot-chess-sets="spotChessSets"
      :is-loading="isUpdatingChessSet"
      @confirm="handleConfirmChessSet"
    />
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GambitType } from '@/core/data/gambit/gambit.type';
import { LocationChessFeature } from '@/core/data/location/location.type';
import type { SegmentProposal, SegmentResult, SparkIntent } from '@/core/data/spark/spark.interface';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import GambitHelper, { pluralize } from '@/core/helpers/gambit.helper';
import LocationHelper from '@/core/helpers/location.helper';
import PlayerHelper from '@/core/helpers/player.helper';
import { isMobile, isPWA, isVerticallyCondensed } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useMapStore } from '@/stores/map.store';
import { useSparkStore } from '@/stores/spark.store';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetCounter from '@/views/components/GamChessSetCounter.vue';
import GamChessSetPopover from '@/views/components/GamChessSetPopover.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useUnpairedChip } from '@/views/composables/hooks/useUnpairedChip';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import InvitePlayers, { type ChosenPlayer } from '@/views/main/gambits/modals/InvitePlayers.vue';
import LocationCover from '@/views/main/sparks/components/LocationCover.vue';
import PlayerAvatar from '@/views/main/sparks/components/PlayerAvatar.vue';
import { findAttendanceConflict, getAttendanceConflictLabel } from '@/views/main/sparks/utils/attendance-conflict';
import { buildProposalShareUrl } from '@/views/main/sparks/utils/proposal-share';
import { addCalendarDaysToDateKey, parseSparkProposalInstant } from '@/views/main/sparks/utils/proposal-time';
import { getLiveSuggestedProposalTime } from '@/views/main/sparks/utils/seeded-suggestion-time';

import { isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

interface SparkMatchProps {
  segment: SegmentResult;
  intent: SparkIntent;
  proposal?: SegmentProposal | null;
  now: Date;
  expiresAt: Date | string;
  onCreateOrJoinProposal: (
    segment: SegmentResult,
    startTime: string,
    endTime: string,
    chessSetCount: number,
  ) => Promise<void>;
  onAcceptInvitation: (proposalId: string, chessSetCount: number) => Promise<void>;
  onLeaveProposal: (proposalId: string, segment: SegmentResult) => Promise<boolean | void>;
  onMeetAnyways: (proposalId: string, segment: SegmentResult) => Promise<boolean | void>;
  onUndoMeetAnyways: (proposalId: string, segment: SegmentResult) => Promise<boolean | void>;
}

const props = defineProps<SparkMatchProps>();

type PlayerSlotUser = SegmentResult['matchedUsers'][number] | SegmentProposal['confirmedUsers'][number];

interface PlayerSlot {
  player: PlayerSlotUser | null;
  isConfirmed: boolean;
  isUpForMeeting?: boolean;
  isInvited?: boolean;
}

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const sparkStore = useSparkStore();
const { myGambits } = storeToRefs(sparkStore);

const router = useRouter();

// Refs

const isSubmitting = ref<boolean>(false);
const chessSetCount = ref<number>(0);
const showChessSetPopover = ref(false);
const editChessSetCount = ref(0);
const isUpdatingChessSet = ref(false);
const localMeetAnywaysOverride = ref<boolean | null>(null);
const showLocationActions = ref(false);

function dismissLocationActions() {
  if (showLocationActions.value) showLocationActions.value = false;
}

onMounted(() => {
  window.addEventListener('scroll', dismissLocationActions, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', dismissLocationActions, true);
});
// const isNarrowLayout = ref<boolean>(window.innerWidth < 414);

// Modal

function getInviteParticipantIds() {
  return [
    ...(props.proposal?.confirmedUsers || []).map((user) => user.id),
    ...(props.proposal?.invitations || [])
      .filter((invitation) => invitation.withdrawn !== true && invitation.accepted !== false)
      .map((invitation) => invitation.userId),
  ];
}

function getInviteInitialPlayers(): ChosenPlayer[] {
  const currentUserId = userDetails.value?.id;
  const players: ChosenPlayer[] = [];
  const seenIds = new Set<string>();

  for (const invitation of props.proposal?.invitations || []) {
    if (invitation.withdrawn === true || invitation.accepted === false) continue;
    if (invitation.invitationOwnerId !== currentUserId) continue;
    if (seenIds.has(invitation.userId)) continue;
    seenIds.add(invitation.userId);
    players.push({
      id: invitation.userId,
      username: invitation.user?.username || '',
      firstName: '',
      lastName: '',
      rating: invitation.user?.rating || 0,
      profileImageBlobHash: invitation.user?.profileImageBlobHash ?? null,
    });
  }

  return players;
}

function getInviteModalAttrs() {
  return {
    proposalId: props.proposal?.id,
    invitationTarget: 'spark' as const,
    participantIds: getInviteParticipantIds(),
    initialPlayers: getInviteInitialPlayers(),
    onClose: handleCloseInvitePlayers,
  };
}

const {
  open: openInviteModal,
  close: closeInviteModal,
  patchOptions: patchInviteModalOptions,
} = useModal({
  component: InvitePlayers,
  attrs: getInviteModalAttrs(),
});

function handleCloseInvitePlayers() {
  closeInviteModal();
}

async function handleJoinAndInvite(playerIds: string[]) {
  if (!props.proposal?.id) return;

  isSubmitting.value = true;
  try {
    await props.onCreateOrJoinProposal(
      props.segment,
      formatInTimeZone(startTime.value, displayTimeZone.value, 'yyyy-MM-dd HH:mm'),
      formatInTimeZone(endTime.value, displayTimeZone.value, 'yyyy-MM-dd HH:mm'),
      chessSetCount.value,
    );

    if (playerIds.length) {
      const result = await gambits.sparkService.createInvitations(props.proposal.id, playerIds);
      if (result.error) {
        useToast().error(result.error.message);
      } else {
        useToast().info(`${playerIds.length > 1 ? 'Players' : 'Player'} invited.`);
      }
    }
  } finally {
    isSubmitting.value = false;
  }
}

function handleOpenInviteModal() {
  if (!props.proposal?.id) return;

  const mode = isJoined.value ? 'send' : 'join-and-invite';

  patchInviteModalOptions({
    attrs: {
      ...getInviteModalAttrs(),
      mode,
      onJoinAndInvite: mode === 'join-and-invite' ? handleJoinAndInvite : undefined,
    },
  });

  openInviteModal();
}

const handleNavigateToPlayerProfile = (username?: string) => {
  if (!username) return;

  router.push({ name: RouterNameType.USERNAME, params: { username }, query: { back: 'history' } });
};

const handleAvatarClick = (player: PlayerSlotUser | null | undefined) => {
  if (!player) return;

  handleNavigateToPlayerProfile(player.username);
};

const handleOpenChessSetPopover = () => {
  editChessSetCount.value = activeChessSetCount.value;
  showChessSetPopover.value = true;
};

const handleConfirmChessSet = async (count: number) => {
  if (!props.proposal?.id) return;

  isUpdatingChessSet.value = true;
  try {
    const response = await sparkStore.updateConfirmationChessSetCount(props.proposal.id, count);
    if (response?.data) {
      showChessSetPopover.value = false;
    }
  } finally {
    isUpdatingChessSet.value = false;
  }
};

const handleNavigateToClub = () => {
  if (!props.proposal?.club?.username) return;

  router.push({
    name: RouterNameType.USERNAME,
    params: { username: props.proposal.club.username },
    query: { back: 'history' },
  });
};

// Computed

const isExpired = computed(() => {
  const expirationDate = typeof props.expiresAt === 'string' ? new Date(props.expiresAt) : props.expiresAt;
  return isAfter(props.now, expirationDate);
});

const displayTitle = computed(() => {
  /* An emptied vote is a suggestion again, so its title leaves with the last face. */
  if (!confirmations.value.length) return null;
  const title = props.proposal?.title;
  if (!title) return null;
  const locName = props.segment?.location?.name;
  if (!locName) return title;
  if (title === locName) return null;
  if (title.startsWith(`${locName} at `)) return null;
  return title;
});

const displayTimeZone = computed(() => {
  return props.segment.timeZone || props.proposal?.timeZone || props.intent.timeZone || 'UTC';
});

const startDateKeyInDisplayZone = computed(() =>
  formatInTimeZone(startTime.value, displayTimeZone.value, 'yyyy-MM-dd'),
);

const todayDateKeyInDisplayZone = computed(() => formatInTimeZone(props.now, displayTimeZone.value, 'yyyy-MM-dd'));

const isDisplayToday = computed(() => startDateKeyInDisplayZone.value === todayDateKeyInDisplayZone.value);

const isDisplayTomorrow = computed(() => {
  const tz = displayTimeZone.value;
  const tomorrowKey = addCalendarDaysToDateKey(todayDateKeyInDisplayZone.value, tz, 1);
  return startDateKeyInDisplayZone.value === tomorrowKey;
});

const sparkDateType = computed(() => {
  return GambitHelper.getGambitsDateType(startTime.value.toISOString(), false, endTime.value.toISOString(), props.now);
});

const dayLabel = computed(() => {
  return formatInTimeZone(startTime.value, displayTimeZone.value, 'EEEE');
});

const proposalShareUrl = computed(() => {
  if (!props.proposal?.id) {
    return '';
  }

  return buildProposalShareUrl(props.proposal.id);
});

const shareProposal = async () => {
  const url = proposalShareUrl.value;

  if (!url) {
    useToast().info('Join first to share!');
    return;
  }

  const toast = useToast();

  const day = isDisplayToday.value ? 'Today' : isDisplayTomorrow.value ? 'Tomorrow' : dayLabel.value;
  const locationName = props.segment.location.name;
  const title = `Chess at ${locationName} — ${day} ${startLabel.value}`;
  const text = `${playersNeeded.value} more ${pluralizedPlayers.value} needed. Join now!`;

  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard.');
  } catch {
    toast.error('Failed to copy URL.');
  }
};

const startLabel = computed(() => {
  return formatInTimeZone(startTime.value, displayTimeZone.value, 'HH:mm');
});

const suggestedProposalTime = computed(() => {
  return getLiveSuggestedProposalTime(props.segment, props.now);
});

const localConfirmations = ref<SegmentProposal['confirmedUsers']>([...(props.proposal?.confirmedUsers || [])]);

const applyLocalMeetAnyways = (enabled: boolean) => {
  const userId = userDetails.value?.id;
  if (!userId) return;

  localConfirmations.value = localConfirmations.value.map((confirmation) =>
    confirmation.id === userId
      ? {
          ...confirmation,
          meetAnywaysAt: enabled ? confirmation.meetAnywaysAt || new Date().toISOString() : null,
        }
      : confirmation,
  );
};

watch(
  () => props.proposal?.confirmedUsers,
  (next) => {
    const incoming = [...(next || [])];
    const userId = userDetails.value?.id;
    const override = localMeetAnywaysOverride.value;

    if (override === null || !userId) {
      localConfirmations.value = incoming;
      return;
    }

    const incomingHasMeetAnyways = !!incoming.find((confirmation) => confirmation.id === userId)?.meetAnywaysAt;

    if (incomingHasMeetAnyways === override) {
      localMeetAnywaysOverride.value = null;
      localConfirmations.value = incoming;
      return;
    }

    localConfirmations.value = incoming.map((confirmation) =>
      confirmation.id === userId
        ? {
            ...confirmation,
            meetAnywaysAt: override ? confirmation.meetAnywaysAt || new Date().toISOString() : null,
          }
        : confirmation,
    );
  },
);

const confirmations = computed(() => localConfirmations.value);

const isHighAspectRatio = computed(() => {
  return false;
});

const startTime = computed(() => {
  if (props.proposal?.start) {
    return parseSparkProposalInstant(props.proposal.start, displayTimeZone.value);
  }

  return suggestedProposalTime.value.start;
});

const endTime = computed(() => {
  if (props.proposal?.end) {
    return parseSparkProposalInstant(props.proposal.end, displayTimeZone.value);
  }

  return suggestedProposalTime.value.end;
});

// const noMatches = computed(() => {
//   return props.segment.matchCount === 1;
// });

const userConfirmation = computed(() => {
  return confirmations.value.find((c) => c.id === userDetails.value?.id);
});

const showMeetAnyways = computed(() => {
  return !!userConfirmation.value && !props.proposal?.isSuccessful;
});

const userHasMeetAnyways = computed(() => {
  if (localMeetAnywaysOverride.value !== null) return localMeetAnywaysOverride.value;
  return !!userConfirmation.value?.meetAnywaysAt;
});

watch(
  () => !!userConfirmation.value?.meetAnywaysAt,
  (hasMeetAnyways) => {
    if (localMeetAnywaysOverride.value !== null && hasMeetAnyways === localMeetAnywaysOverride.value) {
      localMeetAnywaysOverride.value = null;
    }
  },
);

const isJoined = computed(() => {
  return !!userConfirmation.value;
});

const conflictingGambit = computed(() => {
  if (isExpired.value || props.proposal?.isSuccessful) return null;

  return findAttendanceConflict({ start: startTime.value, end: endTime.value }, myGambits.value, [
    props.proposal?.gambitId,
  ]);
});

const isInvitedToProposal = computed(() => {
  const currentUserId = userDetails.value?.id;
  if (!currentUserId || !props.proposal?.invitations || userConfirmation.value) return false;
  return props.proposal.invitations.some(
    (inv) => inv.userId === currentUserId && inv.withdrawn !== true && inv.accepted !== false,
  );
});

const isProposal = computed(() => {
  return !!confirmations.value.length;
});

const prioritizedConfirmations = computed(() => {
  const currentUserId = userDetails.value?.id;

  if (!currentUserId) {
    return confirmations.value;
  }

  const currentUserIndex = confirmations.value.findIndex((user) => user.id === currentUserId);

  if (currentUserIndex <= 0) {
    return confirmations.value;
  }

  const currentUser = confirmations.value[currentUserIndex];

  return [
    currentUser,
    ...confirmations.value.slice(0, currentUserIndex),
    ...confirmations.value.slice(currentUserIndex + 1),
  ];
});

const avatarsCount = computed(() => {
  return 4;
});

const myInvitees = computed((): PlayerSlot[] => {
  const currentUserId = userDetails.value?.id;
  if (!currentUserId) return [];

  const confirmedIds = new Set(confirmations.value.map((u) => u.id).filter(Boolean));
  const slots: PlayerSlot[] = [];

  for (const invitation of props.proposal?.invitations || []) {
    if (invitation.withdrawn === true || invitation.accepted === false) continue;
    if (invitation.invitationOwnerId !== currentUserId) continue;
    if (confirmedIds.has(invitation.userId)) continue;
    if (!invitation.user) continue;

    slots.push({
      player: {
        id: invitation.userId,
        username: invitation.user.username || '',
        rating: invitation.user.rating || 0,
        profileImageBlobHash: invitation.user.profileImageBlobHash ?? null,
      } as PlayerSlotUser,
      isConfirmed: false,
      isInvited: true,
    });
  }

  return slots;
});

const myInviteeCount = computed(() => myInvitees.value.length);

const playerSlots = computed((): PlayerSlot[] => {
  if (isExpired.value) {
    const exlots = [];

    for (let i = 0; i < avatarsCount.value; i++) {
      exlots.push({ player: null, isConfirmed: false });
    }

    return exlots;
  }

  const maxPlayerSlots = avatarsCount.value - 1;

  const slots: PlayerSlot[] = prioritizedConfirmations.value.slice(0, maxPlayerSlots).map((user) => ({
    player: user,
    isConfirmed: true,
    isUpForMeeting: !!user.meetAnywaysAt,
  }));

  if (isInvitedToProposal.value && userDetails.value && slots.length < maxPlayerSlots) {
    const alreadyInSlots = slots.some((s) => s.player?.id === userDetails.value!.id);
    if (!alreadyInSlots) {
      slots.unshift({
        player: {
          id: userDetails.value.id,
          username: userDetails.value.username || '',
          rating: userDetails.value.rating || 0,
          profileImageBlobHash: userDetails.value.profileImageBlobHash ?? null,
        } as PlayerSlotUser,
        isConfirmed: false,
        isInvited: true,
      });
    }
  }

  const remaining = maxPlayerSlots - slots.length;
  if (remaining > 0) {
    for (const invSlot of myInvitees.value.slice(0, remaining)) {
      slots.push(invSlot);
    }
  }

  while (slots.length < maxPlayerSlots) {
    slots.push({
      player: null,
      isConfirmed: false,
    });
  }

  slots.push({ player: null, isConfirmed: false });

  return slots;
});

const playersNeeded = computed(() => {
  if (!confirmations.value.length) return 4;

  return 4 - confirmations.value.length;
});

const proposalGambitTypes = computed(() => {
  if (!confirmations.value.length) return [];
  return [...new Set((props.proposal?.gambitType || []).filter((type) => type !== GambitType.ANY))];
});

const proposalPlayersTag = computed((): GamChipType | null => {
  const isMine = !!userConfirmation.value;
  const others = confirmations.value.length - (isMine ? 1 : 0);
  if (!isMine && others <= 0) return null;

  const chip = {
    size: GamChipSize.X_SMALL,
    leftIcon: GamIconName.PAWN,
    isUpperCase: true,
  };

  if (isMine && others === 0) {
    return { ...chip, label: 'You', variant: GamChipVariant.PRIMARY, isActive: true };
  }

  const players =
    others === 1
      ? localizeWithValues('gambit.detail.tag.player.present', [others])
      : localizeWithValues('gambit.detail.tag.player.multiple', [others]);

  return {
    ...chip,
    label: isMine ? `You & ${players}` : players,
    ...(isMine && { variant: GamChipVariant.PRIMARY, isActive: true }),
  };
});

const proposalRatingTag = computed((): GamChipType | null => {
  const presentation = PlayerHelper.groupRatingPresentation(confirmations.value);
  if (!presentation) return null;

  return {
    ...presentation,
    size: GamChipSize.X_SMALL,
    isUpperCase: true,
    leftIcon: GamIconName.GAMBIT,
  };
});

const unpairedChip = useUnpairedChip(() => confirmations.value.map((confirmation) => confirmation.id));

const proposalBandTags = computed((): GamChipType[] => [
  ...(unpairedChip.value ? [unpairedChip.value] : []),
  ...(proposalPlayersTag.value ? [proposalPlayersTag.value] : []),
  ...(proposalRatingTag.value ? [proposalRatingTag.value] : []),
  ...proposalGambitTypes.value.map((gambitType) => ({
    label: `gambit.detail.tag.category.${gambitType}`,
    size: GamChipSize.X_SMALL,
    isUpperCase: true,
  })),
]);

const isLadiesOnlyProposal = computed(() => {
  return proposalGambitTypes.value.includes(GambitType.LADIES_ONLY);
});

const pluralizedPlayers = computed(() => {
  if (isLadiesOnlyProposal.value) {
    return pluralize('lady', 'ladies', playersNeeded.value);
  } else {
    return pluralize('player', 'players', playersNeeded.value);
  }
});

const isNeededStatus = computed(() => {
  if (isExpired.value) return false;
  if (isInvitedToProposal.value && !userConfirmation.value) return false;
  return confirmations.value.length < 4;
});

const statusLabel = computed((): string | null => {
  if (isExpired.value) return null;
  if (isInvitedToProposal.value && !userConfirmation.value) return 'Invitation';
  if (!isNeededStatus.value) return null;
  if (showMeetAnyways.value) return `${playersNeeded.value} more needed`;
  if (!confirmations.value.length) return `${playersNeeded.value} ${pluralizedPlayers.value} needed`;
  return `${playersNeeded.value} more ${pluralizedPlayers.value} needed`;
});

// Actions

const handleLocationDetailsClick = () => {
  useMapStore().setRingedSpot(props.segment.location.id);
  router.push({
    name: RouterNameType.LOCATIONS,
    query: {
      lat: props.segment.location.coordinates.lat,
      lng: props.segment.location.coordinates.lng,
      zoom: '15',
    },
  });
};

const handleNavigateClick = () => {
  LocationHelper.redirectToGoogleMaps(props.segment.location.coordinates);
};

/* Chess sets stay on this one control: a +/- counter before joining, and once
   joined the same slot shows the count and opens the editor. Avatars are only
   ever profile links. */
const showChessSetAction = computed(() => !isExpired.value && !props.proposal?.isSuccessful);

const activeChessSetCount = computed(() => userConfirmation.value?.chessSetCount ?? 0);

const playerChessSets = computed(() =>
  (props.proposal?.confirmedUsers || []).reduce((sum, user) => sum + (user.chessSetCount ?? 0), 0),
);

const spotChessSets = computed(() => {
  const location = props.proposal?.location || props.segment.location;
  const features = location?.chessFeatures || [];
  if (features.length > 0 && !features.includes(LocationChessFeature.CHESS_SET)) return 0;
  return location?.chessSetCount || 0;
});

/** Fewer boards than one per pair of players, counting the spot's own. Same read as the gambit card. */
const isShortOfChessSets = computed(
  () => playerChessSets.value + spotChessSets.value < Math.ceil(confirmations.value.length / 2),
);

const handleCreateOrJoinProposal = async (segment: SegmentResult, startTime: Date, endTime: Date) => {
  isSubmitting.value = true;
  await props.onCreateOrJoinProposal(
    segment,
    formatInTimeZone(startTime, displayTimeZone.value, 'yyyy-MM-dd HH:mm'),
    formatInTimeZone(endTime, displayTimeZone.value, 'yyyy-MM-dd HH:mm'),
    chessSetCount.value,
  );
  isSubmitting.value = false;
};

const handleAcceptInvitation = async () => {
  if (!props.proposal?.id) return;
  isSubmitting.value = true;
  await props.onAcceptInvitation(props.proposal.id, chessSetCount.value);
  isSubmitting.value = false;
};

const handleLeaveProposal = async () => {
  if (!props.proposal) return;

  isSubmitting.value = true;

  const previousConfirmations = [...localConfirmations.value];
  const userId = userDetails.value?.id;
  if (userId) {
    localConfirmations.value = localConfirmations.value.filter((c) => c.id !== userId);
  }

  const didSucceed = await props.onLeaveProposal(props.proposal.id, props.segment);
  if (didSucceed === false) {
    localConfirmations.value = previousConfirmations;
  }
  isSubmitting.value = false;
};

const handleMeetAnyways = async () => {
  if (!props.proposal) return;

  const previousMeetAnywaysAt = userConfirmation.value?.meetAnywaysAt ?? null;
  const toggled = !userHasMeetAnyways.value;
  localMeetAnywaysOverride.value = toggled;
  applyLocalMeetAnyways(toggled);
  isSubmitting.value = true;

  const didSucceed = toggled
    ? await props.onMeetAnyways(props.proposal.id, props.segment)
    : await props.onUndoMeetAnyways(props.proposal.id, props.segment);

  if (didSucceed === false) {
    localMeetAnywaysOverride.value = null;
    const userId = userDetails.value?.id;
    if (userId) {
      localConfirmations.value = localConfirmations.value.map((confirmation) =>
        confirmation.id === userId ? { ...confirmation, meetAnywaysAt: previousMeetAnywaysAt } : confirmation,
      );
    }
  } else if (!!userConfirmation.value?.meetAnywaysAt === toggled) {
    localMeetAnywaysOverride.value = null;
  }

  isSubmitting.value = false;
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.SparkCard {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;

  /* The two panels are separate objects, so the card itself carries no surface,
     no clipping and no shadow — each child draws its own. */
  gap: var(--card-stack-gap);
  background: transparent;
  overflow: visible;
}

/* The deck sets a whole-card shadow on `.spark-card`, which predates this card
   being split into two panels. Opting out by compound selector rather than
   relying on which scoped stylesheet the bundler emits last. */
.SparkCard.spark-card {
  box-shadow: none;
}

.SparkCard.is-expired {
  filter: saturate(0);
}

.cover {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  border-radius: var(--radius-normal);
  overflow: hidden;
  background: var(--color-dark-600);
  cursor: pointer;
}

.cover-foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem var(--card-gutter) var(--card-foot-inset);
  background: linear-gradient(transparent 0%, rgba(28, 28, 28, 0.75) 100%);
  pointer-events: none;
}

.cover-foot > * {
  pointer-events: auto;
}

/* Bottom-aligned: a filled seat carries a badge row above its face, so centring
   would drop that face below the empty rings beside it. */
.cover-players {
  display: flex;
  align-items: flex-end;
  gap: clamp(0.25rem, min(1.75cqw, 1.17cqh), 0.5rem);
}

.seat {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  width: var(--card-avatar-size);
  min-height: 0;
}

.cover-players :deep(.PlayerAvatar) {
  cursor: pointer;
}

/* Grey faces vanish into the cover photo. The ring lifts them off dark imagery
   and the shadow off light imagery; kept in sync with GambitCard and the week card. */
.cover-players :deep(.PlayerAvatar:not(.is-invited) .avatar) {
  box-shadow:
    0 0 0 1.5px var(--color-white-60),
    0 6px 16px rgb(0 0 0 / 0.55);
}

.seat-ring {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Stronger than the shelf rings — these sit on a photo, so the backdrop varies
     and a fainter edge disappears over light imagery. */
  border: 1px solid var(--color-white-20);
  color: var(--color-white-40);
}

.seat-ring.is-invite {
  border-style: dashed;
  border-color: var(--color-white-30);
  color: var(--color-white-60);
  cursor: pointer;
  transition: color 0.15s ease;
}
@include hover-capable {
  .seat-ring.is-invite:hover {
    color: var(--color-white);
  }
}

.seat-count {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.cover-actions {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  z-index: 6;
}

.meta {
  flex: none;
  height: max(var(--card-meta-min), calc(50cqw - var(--card-stack-gap)));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(0.4rem, min(2.5cqw, 1.67cqh), 0.75rem);
  padding: var(--card-gutter);
  border-radius: var(--radius-normal);
  /* Flat: the surface token already sits light enough against the page to define
     its own edge, so a shadow would only restate it. */
  background: var(--color-card-surface);

  &.needs-respond {
    border: 2px solid var(--color-pink);
  }
}

.meta-head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.when {
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  font-size: clamp(1.05rem, min(6cqw, 4cqh), 1.5rem);
  letter-spacing: 0.02em;
  color: var(--color-white);
  white-space: nowrap;
}

.when .day-live {
  color: var(--color-pink);
  font-weight: 600;
}

.sub {
  display: flex;
  align-items: baseline;
  gap: 0.35em;
  min-width: 0;
  font-size: clamp(0.75rem, min(4cqw, 2.67cqh), 0.875rem);
  letter-spacing: 0.02em;
  color: var(--color-white-50);
}

.sub .sep,
.when .sep {
  color: var(--color-white-30);
  flex: none;
}

.sub .spot {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.conflict-warning {
  display: flex;
  align-items: center;
  gap: 0.3em;
  min-width: 0;
  font-size: clamp(0.7rem, min(3.6cqw, 2.4cqh), 0.8rem);
  color: var(--color-orange);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.sub .status {
  color: var(--color-orange);
  font-weight: 600;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  .dot {
    width: 6px;
    height: 6px;
    min-width: 6px;
    border-radius: 50%;
    background-color: currentColor;
  }
}

/* The auto column is centred in the grid regardless of what the 1fr columns hold,
   which keeps play on the card's axis whether or not Meet Anyways renders. */
.controls {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.tertiary,
.primary,
.secondary {
  display: flex;
  align-items: center;
}

.tertiary {
  grid-column: 1;
  justify-self: start;
  min-width: 0;
}

/* Matches the gambit card's chess-set button, which opens the same editor. */
.chess-set-action {
  width: var(--card-meet-size);
  height: var(--card-meet-size);
  @include toolbar-action;
}

.primary {
  grid-column: 2;
  justify-self: center;
}

/* Snug to play like a next-track button, rather than pinned to the far edge. */
.secondary {
  grid-column: 3;
  justify-self: start;
  padding-left: clamp(0.75rem, 4cqw, 1.25rem);
}

.meet-anyways {
  position: relative;
  display: flex;
  align-items: center;
}

/* Out of flow, so the hint's width can't widen this cell and nudge the button
   off the alignment it holds with play. */
.meet-anyways-hint {
  position: absolute;
  top: calc(100% + 0.2rem);
  left: 50%;
  transform: translateX(-50%);
  font-size: clamp(0.625rem, min(3.2cqw, 2.1cqh), 0.6875rem);
  line-height: 1.1;
  letter-spacing: 0.02em;
  color: var(--color-white-50);
  white-space: nowrap;
  text-align: center;
  pointer-events: none;
}

/* Short viewports leave less room than the gutter below the controls row. */
.meet-anyways.hint-above .meet-anyways-hint {
  top: auto;
  bottom: calc(100% + 0.2rem);
}

/* Icon Play is a disc. A text Accept matches that height and keeps its own width. */
.primary :deep(.gam-button-container.is-icon) {
  width: var(--card-play-size) !important;
  height: var(--card-play-size) !important;
  min-width: var(--card-play-size) !important;
  min-height: var(--card-play-size) !important;
}

.primary :deep(.gam-button-container:not(.is-icon)) {
  height: var(--card-play-size) !important;
  min-height: var(--card-play-size) !important;
}

.secondary :deep(.gam-button-container.is-icon) {
  width: var(--card-meet-size) !important;
  height: var(--card-meet-size) !important;
  min-width: var(--card-meet-size) !important;
  min-height: var(--card-meet-size) !important;
}

/* GamIcon sizes its SVG in em off this span's font-size, set inline, so an
   !important override scales the glyph and keeps its aspect ratio. */
.primary :deep(.svg-container) {
  font-size: var(--card-play-icon) !important;
  width: var(--card-play-icon) !important;
  height: var(--card-play-icon) !important;
  min-width: var(--card-play-icon) !important;
}

.secondary :deep(.svg-container) {
  font-size: var(--card-meet-icon) !important;
  width: var(--card-meet-icon) !important;
  height: var(--card-meet-icon) !important;
  min-width: var(--card-meet-icon) !important;
}

.expired-notice {
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-white-50);
}

.skeleton-button {
  width: var(--card-play-size);
  height: var(--card-play-size);
  border-radius: 50%;
  background: var(--color-white-20);
}

.SparkCard .progress-row {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  backdrop-filter: blur(8px);
  background: var(--color-white-2);
  z-index: 5;
}

.SparkCard .progress {
  padding: 0.75rem 0.5rem;
  width: 100%;
}

.location-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  mask-image: linear-gradient(black 0%, black 75%, transparent 100%);
}

.icon-circle :deep(.gam-button-container) {
  border-radius: 50% !important;

  .icon {
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }

  .svg-container {
    font-size: 24px !important;
    width: 24px !important;
    height: 24px !important;
  }
}

@include play-cta;

.card-action {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-30);
  transition: color 0.15s ease;
}
@include hover-capable {
  .card-action:hover {
    color: var(--color-white-70);
  }
}

.invite-flip {
  transform: scaleX(-1);
}

.location-actions-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
}

.location-actions-enter-active,
.location-actions-leave-active {
  transition: opacity 0.15s ease;
}

.location-actions-enter-from,
.location-actions-leave-to {
  opacity: 0;
}

.location-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  color: var(--color-white-80);
  cursor: pointer;

  span {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.03em;
  }

  &:active {
    color: var(--color-white);
  }
}

.chip-band {
  flex: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  height: var(--card-band-height);
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
}

.band-title {
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: var(--spacing-xxs);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-white);
}

.sub .club-link {
  color: var(--color-white-50);
  cursor: pointer;
  white-space: nowrap;
  flex: none;
}
@include hover-capable {
  .sub .club-link:hover {
    text-decoration: underline;
  }
}
</style>
